/*
 * useWheel keeps a horizontal wheel of orbs balanced.
 *
 * The track is padded so that its first and last orbs can reach the middle,
 * which left the wheel opening with its left half empty and every orb crowded
 * to the right. It opens with its middle in the middle instead, unless that
 * would hide the orb already picked, and an orb the operator picks is brought
 * to the centre. Only the track scrolls: scrollIntoView would move the page too.
 */

import { useLayoutEffect, useRef } from "react";

// The track fades its outer eighths away, so an orb there is not really shown.
const FADED = 0.12;

const orbOf = (track, key) =>
  key ? [...track.querySelectorAll("[data-orb]")].find((el) => el.dataset.orb === key) || null : null;

const centreOf = (track, orb) => {
  const t = track.getBoundingClientRect();
  const o = orb.getBoundingClientRect();
  return o.left - t.left + track.scrollLeft + o.width / 2 - track.clientWidth / 2;
};

function scrollTrack(track, left, smooth) {
  if (smooth && typeof track.scrollTo === "function") {
    track.scrollTo({ left, behavior: "smooth" });
    return;
  }
  // The track's own CSS asks for smooth scrolling; opening must not animate.
  const was = track.style.scrollBehavior;
  track.style.scrollBehavior = "auto";
  track.scrollLeft = left;
  track.style.scrollBehavior = was;
}

/** useWheel(selectedKey, itemsKey) returns the ref for the scrolling track.
    Each orb carries data-orb={its key}. itemsKey changes when the orbs do. */
export default function useWheel(selectedKey, itemsKey) {
  const trackRef = useRef(null);
  const shownKey = useRef(undefined);
  const balancedFor = useRef(null);

  // Balanced on opening, and again when a search changes which orbs there are.
  // Checked after every render rather than on itemsKey alone: a panel that
  // reads its orbs before it draws the track (QoS Policies) mounts the track a
  // render later, and the wheel must still open balanced then.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || balancedFor.current === itemsKey) return;
    balancedFor.current = itemsKey;
    let left = (track.scrollWidth - track.clientWidth) / 2;
    const picked = orbOf(track, selectedKey);
    if (picked) {
      const at = centreOf(track, picked) - left; // its offset from the middle once balanced
      if (Math.abs(at) > track.clientWidth * (0.5 - FADED)) left += at;
    }
    scrollTrack(track, left, false);
    shownKey.current = selectedKey;
  });

  // A pick brings its orb to the centre.
  useLayoutEffect(() => {
    if (shownKey.current === selectedKey) return;
    shownKey.current = selectedKey;
    const track = trackRef.current;
    const orb = track && orbOf(track, selectedKey);
    if (orb) scrollTrack(track, centreOf(track, orb), true);
  }, [selectedKey]);

  return trackRef;
}
